import { type Event } from '@sentry/react';

import { getTeamAndModuleFromUrl } from './urlToTeamMapping';

/**
 * Sets the team tag on a Sentry event based on the request URL (fallback only)
 *
 * This helper automatically tags errors with the team responsible for the URL,
 * based on CODEOWNERS mappings. It helps ensure that errors from non-React
 * (HAML) pages are properly tagged with team ownership.
 *
 * IMPORTANT: This only applies if the event doesn't already have a team tag.
 * ErrorBoundary-tagged errors take precedence over URL-based mapping.
 *
 * @param event - Sentry event to tag
 * @returns Modified event with team tag (if applicable)
 */
const setTeamTag = (event: Event): Event => {
  // Only apply URL-based team mapping if team tag doesn't already exist
  // (ErrorBoundary tags take precedence)
  if (event.tags?.team) {
    return event;
  }

  if (event.request?.url) {
    const teamAndModule = getTeamAndModuleFromUrl(event.request.url);

    if (teamAndModule) {
      event.tags = {
        ...(event.tags ?? {}),
        team: teamAndModule.team,
      };

      // Also add module tag if we extracted one and it doesn't exist
      if (teamAndModule.module && !event.tags.module) {
        event.tags.module = teamAndModule.module;
      }
    }
  }

  return event;
};

export default setTeamTag;
